/**
 * Stable editor identity for list/trip stops (post_locations rows).
 * Never key notes by location_id, Google placeId, or array index.
 */

import type PostLocation from '@/models/postLocation';
import { generateTempListSlotId, generateTempPlaceId, isMongoObjectIdString } from '@/utils/idGeneration';

export const CLIENT_STOP_ID_KEY = '_clientId' as const;

/** Session-stable id for a new stop before the server assigns post_locations._id. */
export function generateClientStopId(): string {
  return generateTempPlaceId(undefined);
}

/** Session-stable id for a new list slot. */
export function generateClientListStopId(): string {
  return generateTempListSlotId(undefined);
}

type PostLocationLike = {
  itinerary_day_index?: number;
  itinerary_day_order_index?: number;
  order?: number;
};

/** Sort itinerary post_locations for hydrate/save (day, then order). */
export function sortItineraryPostLocations<T extends PostLocationLike>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const dayA = a.itinerary_day_index ?? -1;
    const dayB = b.itinerary_day_index ?? -1;
    if (dayA !== dayB) return dayA - dayB;
    const orderA = a.itinerary_day_order_index ?? -1;
    const orderB = b.itinerary_day_order_index ?? -1;
    return orderA - orderB;
  });
}

/** Sort list post_locations for hydrate/save (order field, then stable index). */
export function sortListPostLocations<T extends PostLocationLike>(rows: T[]): T[] {
  return [...rows].map((row, index) => ({ row, index })).sort((a, b) => {
    const orderA = a.row.order ?? a.row.itinerary_day_order_index ?? -1;
    const orderB = b.row.order ?? b.row.itinerary_day_order_index ?? -1;
    if (orderA !== orderB) return orderA - orderB;
    return a.index - b.index;
  }).map(({ row }) => row);
}

/** Editor id carried on slots/places for update matching. */
export function getStopIdFromRecord(record: {
  _clientId?: string | null;
  _id?: string | null;
  postLocationId?: string | null;
  id?: string | null;
}): string | null {
  const client = record._clientId;
  if (typeof client === 'string' && client.length > 0) return client;
  if (typeof record.id === 'string' && record.id.length > 0) return record.id;
  if (typeof record._id === 'string' && record._id.length > 0) return record._id;
  if (typeof record.postLocationId === 'string' && record.postLocationId.length > 0) {
    return record.postLocationId;
  }
  return null;
}

/** True when `stopId` refers to this slot/place row (exact id match only). */
export function stopIdsMatch(
  record: {
    _clientId?: string | null;
    _id?: string | null;
    postLocationId?: string | null;
    id?: string | null;
  },
  stopId: string
): boolean {
  const target = String(stopId);
  const candidates = [
    record._clientId,
    record.id,
    record._id,
    record.postLocationId,
  ].filter((v): v is string => typeof v === 'string' && v.length > 0);
  return candidates.some((c) => String(c) === target);
}

/**
 * post_locations._id to send on list save — never the Location document _id.
 */
export function resolveListPostLocationIdForSave(
  rawSlotPostLocId: unknown,
  locationDocumentMongoId: string | null
): string | null {
  if (typeof rawSlotPostLocId !== 'string' || !isMongoObjectIdString(rawSlotPostLocId)) {
    return null;
  }
  if (locationDocumentMongoId && String(rawSlotPostLocId) === String(locationDocumentMongoId)) {
    return null;
  }
  return rawSlotPostLocId;
}

/** Key for remapping server ids after trip save: day + sequence sent in payload. */
export function itineraryStopOrderKey(dayIndex: number, sequenceIndex: number): string {
  return `${dayIndex}:${sequenceIndex}`;
}

export type ItineraryStopOrderKey = ReturnType<typeof itineraryStopOrderKey>;

/** Build order-key → post_location _id from save response rows. */
export function mapItineraryPostLocationIdsByOrder(
  rows: Array<Pick<PostLocation, 'itinerary_day_index' | 'itinerary_day_order_index' | '_id'>>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of sortItineraryPostLocations(rows)) {
    if (!row._id) continue;
    const day = row.itinerary_day_index ?? 0;
    const seq = row.itinerary_day_order_index ?? 0;
    map.set(itineraryStopOrderKey(day, seq), String(row._id));
  }
  return map;
}

type ListSlotLike = {
  sequenceIndex?: number;
  _clientId?: string;
  _id?: string;
  postLocationId?: string;
  notes?: string;
  locationId?: string;
  location_id?: string;
  [key: string]: unknown;
};

/** Merge server post_location ids onto editor slots without replacing notes or order. */
export function mergeListSlotsAfterSave<T extends ListSlotLike>(
  prevSlots: T[],
  savedPostLocations: Array<{ _id?: string; order?: number; notes?: string }>
): T[] {
  const sorted = sortListPostLocations(savedPostLocations);
  if (prevSlots.length === 0) {
    return prevSlots;
  }

  return prevSlots.map((slot) => {
    const seq = typeof slot.sequenceIndex === 'number' ? slot.sequenceIndex : -1;
    const savedRow = sorted.find((row, idx) => {
      const rowOrder = row.order ?? idx;
      return rowOrder === seq;
    });
    if (!savedRow?._id) {
      return slot;
    }
    return {
      ...slot,
      _id: savedRow._id,
      postLocationId: savedRow._id,
    };
  });
}

type ItinerarySlotLike = {
  dayIndex?: number;
  sequenceIndex?: number;
  _clientId?: string;
  _id?: string;
  postLocationId?: string;
  notes?: string;
  [key: string]: unknown;
};

export function remapItinerarySlotsAfterSave<T extends ItinerarySlotLike>(
  slots: T[],
  savedPostLocations: Array<Pick<PostLocation, 'itinerary_day_index' | 'itinerary_day_order_index' | '_id'>>
): T[] {
  const idByOrder = mapItineraryPostLocationIdsByOrder(savedPostLocations);
  return slots.map((slot) => {
    const day = slot.dayIndex ?? 0;
    const seq = slot.sequenceIndex ?? 0;
    const newId = idByOrder.get(itineraryStopOrderKey(day, seq));
    if (!newId) return slot;
    return {
      ...slot,
      _id: newId,
      postLocationId: newId,
    };
  });
}

type TimelinePlaceLike = {
  id?: string;
  dayIndex?: number;
  sequenceIndex?: number;
  postLocationId?: string;
  _clientId?: string;
  notes?: string;
  [key: string]: unknown;
};

export type TimelineDayLike = {
  places?: TimelinePlaceLike[];
  [key: string]: unknown;
};

/** Resolve editor stop id for a timeline card (matches drag ids and updatePlace). */
export function getTimelinePlaceStopId(place: {
  id?: string | null;
  postLocationId?: string | null;
  _clientId?: string | null;
}): string | undefined {
  const id = place.id || place.postLocationId || place._clientId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

function timelineDayForIndex(timeline: TimelineDayLike[], dayIndex: number): TimelineDayLike | undefined {
  const tagged = timeline.find((day) => day.index === dayIndex);
  return tagged ?? timeline[dayIndex];
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function nestedRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function readLocationId(record: Record<string, unknown>): string {
  const location = nestedRecord(record.location);
  const locationData = nestedRecord(record.locationData);
  return (
    readString(record.locationId) ||
    readString(record.location_id) ||
    readString(location?._id) ||
    readString(locationData?._id)
  );
}

function readGooglePlaceId(record: Record<string, unknown>): string {
  const location = nestedRecord(record.location);
  const locationData = nestedRecord(record.locationData);
  return (
    readString(record.placeId) ||
    readString(record.google_place_id) ||
    readString(location?.google_place_id) ||
    readString(locationData?.google_place_id)
  );
}

/**
 * A save deletes post_location rows and inserts new ids, so those ids cannot
 * match a card. Position is also wrong: two copies can share an order number
 * and both receive the previous card's title. Match the place document on that day.
 * A session client id still matches a stop that has no location yet.
 */
function findTimelinePlaceForSlot<TPlace extends TimelinePlaceLike>(
  slot: ItinerarySlotLike,
  timeline: TimelineDayLike[],
  dayIndex: number
): { place: TPlace; matchedByLocation: boolean } | undefined {
  const day = timelineDayForIndex(timeline, dayIndex);
  const places = day?.places;
  if (!places?.length) return undefined;

  const slotLocationId = readLocationId(slot as Record<string, unknown>);
  if (slotLocationId) {
    const byLocation = places.filter(
      (place) => readLocationId(place as Record<string, unknown>) === slotLocationId,
    );
    if (byLocation.length === 1) {
      return { place: byLocation[0] as TPlace, matchedByLocation: true };
    }
  }

  const clientId = readString(slot._clientId);
  if (clientId) {
    const byClient = places.find((place) => readString(place._clientId) === clientId);
    if (byClient) return { place: byClient as TPlace, matchedByLocation: false };
  }

  return undefined;
}

type StopRecord = {
  _clientId?: string | null;
  _id?: string | null;
  postLocationId?: string | null;
  id?: string | null;
  sequenceIndex?: number;
};

/**
 * Indices of the rows an edit for `stopId` applies to. Two stops can carry the same id
 * (loader id fallbacks, temp ids), and an edit on one card then also landed on the other.
 * When several rows match, `target` (the edited card) picks one: the same object, else the
 * same place and position, else the same place, else the same position. If none of those
 * narrows it to one row, every match is returned as before.
 */
export function resolveStopMatchIndices<T extends StopRecord>(
  rows: T[],
  stopId: string,
  target?: StopRecord | null,
  isCandidate: (row: T) => boolean = () => true,
): number[] {
  const matches: number[] = [];
  rows.forEach((row, index) => {
    if (row && isCandidate(row) && stopIdsMatch(row, stopId)) matches.push(index);
  });
  if (matches.length <= 1 || !target) return matches;

  const byReference = matches.filter((index) => rows[index] === target);
  if (byReference.length === 1) return byReference;

  const targetLocationId = readLocationId(target as Record<string, unknown>);
  const targetSequence = typeof target.sequenceIndex === 'number' ? target.sequenceIndex : null;
  const samePlace = (index: number) =>
    !!targetLocationId && readLocationId(rows[index] as Record<string, unknown>) === targetLocationId;
  const samePosition = (index: number) =>
    targetSequence !== null && rows[index].sequenceIndex === targetSequence;

  for (const keep of [
    (index: number) => samePlace(index) && samePosition(index),
    samePlace,
    samePosition,
  ]) {
    const narrowed = matches.filter(keep);
    if (narrowed.length === 1) return narrowed;
  }
  return matches;
}

function locationNameOf(record: Record<string, unknown>): string {
  const location = nestedRecord(record.location);
  const locationData = nestedRecord(record.locationData);
  return readString(locationData?.name) || readString(location?.name);
}

function titleIsAnotherPlaceOnDay(
  slot: ItinerarySlotLike,
  timeline: TimelineDayLike[],
  dayIndex: number,
): boolean {
  const slotRecord = slot as Record<string, unknown>;
  const title = readString(slotRecord.name) || readString(slotRecord.body);
  const ownName = locationNameOf(slotRecord);
  if (!title || title === ownName) return false;
  const places = timelineDayForIndex(timeline, dayIndex)?.places ?? [];
  const ownLocationId = readLocationId(slotRecord);
  return places.some((place) => {
    const placeRecord = place as Record<string, unknown>;
    if (ownLocationId && readLocationId(placeRecord) === ownLocationId) return false;
    const otherTitle = readString(placeRecord.title) || readString(placeRecord.name);
    const otherLocation = locationNameOf(placeRecord);
    return otherTitle === title || otherLocation === title;
  });
}

type DedupeStop = {
  dayIndex?: number;
  itinerary_day_index?: number;
  [key: string]: unknown;
};

function stopPlaceIdentity(slot: DedupeStop): string | null {
  const record = slot as Record<string, unknown>;
  return readLocationId(record) || readGooglePlaceId(record) || null;
}

function stopDayIndex(slot: DedupeStop): number {
  return slot.dayIndex ?? slot.itinerary_day_index ?? 0;
}

/**
 * Drop a second copy of the same place on the same day.
 * The editor can hold each stop twice; a full-replace save would store both.
 */
export function dedupeItinerarySlotsByPlace<T>(slots: T[]): T[] {
  const seen = new Set<string>();
  const kept: T[] = [];
  for (const slot of slots) {
    const identity = stopPlaceIdentity(slot as DedupeStop);
    if (!identity) {
      kept.push(slot);
      continue;
    }
    const key = `${stopDayIndex(slot as DedupeStop)}:${identity}`;
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(slot);
  }
  return kept;
}

/**
 * Before a trip save, copy inline-edited fields from timeline places onto itinerary slots.
 * Timeline is what the user sees; slots feed postLocations on POST /api/v2/posts.
 */
export function mergeItinerarySlotsFromTimeline<TSlot extends ItinerarySlotLike>(
  slots: TSlot[],
  timeline: TimelineDayLike[]
): TSlot[] {
  if (!slots.length || !timeline.length) {
    return slots;
  }

  return slots.map((slot) => {
    const dayIndex = slot.dayIndex ?? 0;
    const found = findTimelinePlaceForSlot<TimelinePlaceLike>(slot, timeline, dayIndex);
    const patch: Partial<TSlot> = {};

    if (found) {
      const { place, matchedByLocation } = found;

      if (typeof place.notes === 'string') {
        patch.notes = place.notes;
      }

      // Copy a title only for a stop that has no location document yet.
      // A Google place's subtitle is its own location name, not another card's title.
      if (!matchedByLocation) {
        const title =
          typeof place.title === 'string'
            ? place.title
            : typeof place.name === 'string'
              ? place.name
              : undefined;
        if (title !== undefined) {
          (patch as Record<string, unknown>).name = title;
          (patch as Record<string, unknown>).body = title;
        }
      }

      if (typeof place.address === 'string') {
        (patch as Record<string, unknown>).address = place.address;
      }

      const timeRange =
        typeof place.timeRange === 'string'
          ? place.timeRange
          : typeof place.time === 'string'
            ? place.time
            : undefined;
      if (timeRange !== undefined) {
        (patch as Record<string, unknown>).timeRange = timeRange;
        (patch as Record<string, unknown>).time = timeRange;
      }

      const cost = place.cost ?? place.budget;
      if (cost !== undefined && cost !== null && cost !== '') {
        (patch as Record<string, unknown>).budget = cost;
        (patch as Record<string, unknown>).cost = cost;
      }
    }

    const merged = { ...slot, ...patch };
    const ownName = locationNameOf(merged as Record<string, unknown>);
    const userCreated = merged.isUserCreated === true;
    if (!userCreated && ownName && titleIsAnotherPlaceOnDay(merged, timeline, dayIndex)) {
      (merged as Record<string, unknown>).name = ownName;
      (merged as Record<string, unknown>).body = ownName;
    }

    if (Object.keys(patch).length === 0 && merged.name === slot.name && merged.body === slot.body) {
      return slot;
    }
    return merged;
  });
}

/** True when API `locations` is an array of post_location rows (itinerary save response). */
export function isPostLocationRowArray(rows: unknown): rows is Array<Record<string, unknown>> {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const first = rows[0];
  if (!first || typeof first !== 'object') return false;
  return (
    'itinerary_day_index' in first ||
    'itinerary_day_order_index' in first ||
    ('notes' in first && ('location_id' in first || 'body' in first))
  );
}

/** post_location rows from a save/get Post (handles itinerary save `locations` key). */
export function getPostLocationRowsFromPost(post: {
  postLocations?: PostLocation[];
  post_locations?: PostLocation[];
  locations?: unknown;
}): PostLocation[] {
  if (post.postLocations?.length) {
    return post.postLocations;
  }
  if (post.post_locations?.length) {
    return post.post_locations;
  }
  if (isPostLocationRowArray(post.locations)) {
    return post.locations.map((row) => row as unknown as PostLocation);
  }
  return [];
}

export function remapTimelinePlacesAfterSave<TDay extends TimelineDayLike>(
  timeline: TDay[],
  savedPostLocations: Array<Pick<PostLocation, 'itinerary_day_index' | 'itinerary_day_order_index' | '_id'>>
): TDay[] {
  const idByOrder = mapItineraryPostLocationIdsByOrder(savedPostLocations);
  return timeline.map((day, dayIndex) => {
    const places = day.places?.map((place, placeIndex) => {
      const seq =
        typeof place.sequenceIndex === 'number' ? place.sequenceIndex : placeIndex;
      const di = typeof place.dayIndex === 'number' ? place.dayIndex : dayIndex;
      const newId = idByOrder.get(itineraryStopOrderKey(di, seq));
      if (!newId) return place;
      const clientId = place._clientId || place.id || newId;
      return {
        ...place,
        id: clientId,
        postLocationId: newId,
        _clientId: clientId,
      };
    });
    return { ...day, places };
  });
}
